import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const EnvironmentalIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Self-Cleaning Suction Screens',
      description: 'Surface-water withdrawals for restoration projects',
      link: '/filtration/suction-screens/self-cleaning'
    },
    {
      title: 'Passive Fish Screens',
      description: 'Fish-screen and debris-exclusion compliance',
      link: '/filtration/suction-screens/passive-fish-screens'
    },
    {
      title: 'Inline Pressure Filters',
      description: 'Monitoring stations and environmental research installations',
      link: '/filtration/inline/big'
    }
  ];

  const benefits = [
    'Self-cleaning suction screens for surface-water withdrawals and restoration projects',
    'Inline filtration for monitoring stations and environmental research installations',
    'Engineered for compliance with fish-screen and debris-exclusion requirements',
    'Stainless-steel construction for long-term environmental installations',
    'Custom configurations for variable flow conditions',
    'Low-maintenance designs for remote monitoring sites'
  ];

  const applications = [
    {
      title: 'Surface-Water Intakes',
      description: 'Protect pumps and equipment while meeting environmental compliance standards for fish protection and debris exclusion in streams and rivers.'
    },
    {
      title: 'Habitat Restoration',
      description: 'Support watershed restoration projects with filtration systems designed to maintain ecosystem balance and protect native species.'
    },
    {
      title: 'Stormwater Treatment',
      description: 'Filter stormwater runoff for reuse or discharge, removing sediment and debris while meeting regulatory requirements.'
    },
    {
      title: 'Environmental Sampling',
      description: 'Provide consistent, clean water supply for monitoring stations, research installations, and water-quality assessment programs.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Environmental Surface Water Filtration Systems | CTC Waterworks"
        description="Engineered filtration systems for stormwater, habitat restoration, and environmental monitoring applications."
        canonicalPath="/industries/environmental"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-700 to-green-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Leaf className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Environmental Surface Water
                  </h1>
                </div>
                <p className="text-2xl text-green-100 mb-6 font-light leading-relaxed">
                  From watershed management to stormwater reuse, CTC filtration systems help maintain ecosystem balance and infrastructure reliability.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-green-100">Self-cleaning suction screens for surface-water withdrawals and restoration projects</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-green-100">Inline filtration for monitoring stations and environmental research installations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-green-100">Engineered for compliance with fish-screen and debris-exclusion requirements</span>
                  </li>
                </ul>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=environmental-filtration"
                  className="block w-full bg-green-700 text-white px-6 py-4 rounded-lg font-semibold hover:bg-green-800 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Get design assistance, submittals, and spec packs for your next project.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Environmental Surface Water Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {applications.map((app, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-green-50 rounded-lg p-6 border border-green-200 hover:border-green-700 hover:shadow-lg transition-all"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{app.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Recommended Filtration Systems
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {systems.map((system, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={system.link}
                    className="block bg-white rounded-lg shadow-lg p-6 h-full hover:shadow-xl transition-all border-2 border-transparent hover:border-green-700 group"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-700">
                      {system.title}
                    </h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">{system.description}</p>
                    <span className="inline-flex items-center text-green-700 font-semibold">
                      Learn More <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Why CTC for Environmental Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <CheckCircle className="text-green-700 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 text-lg">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-green-700 to-green-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Let's Engineer Your Solution</h2>
            <p className="text-xl text-green-100 mb-8">
              From surface-water intakes to habitat restoration, our systems are built to protect ecosystems and maintain infrastructure reliability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?topic=environmental-filtration"
                className="inline-flex items-center justify-center bg-white text-green-700 px-8 py-4 rounded-lg font-semibold hover:bg-green-50 transition-colors shadow-lg"
              >
                <span>Talk to a Filtration Expert</span>
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <a
                href="tel:+15099017476"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-green-700 transition-colors"
              >
                <span>(509) 901-7476</span>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default EnvironmentalIndustryPage;
